<script setup>
import MarketNoteMark from 'components/market/MarketNoteMark.vue';
import { formatMarketMetric } from 'src/utils/market-util';
import { computed } from 'vue';

const $props = defineProps({
    trend: {
        type: Object,
        default: null
    }
});

/**
 * The figure is a linear trend fitted across the daily snapshots in the window and scaled to a month, so it names a
 * rate of movement. It is not the difference between the count today and a count taken thirty days ago, and the
 * window can hold as few as seven days while snapshots are still accumulating, so the label says "a month" rather
 * than "over 30 days" and the basis is carried in the title.
 */
const isUnavailable = computed(() => {
    return !Number.isFinite($props.trend?.changePercent) || !['up', 'down', 'stable'].includes($props.trend?.direction);
});

const trendPresentation = computed(() => {
    if (isUnavailable.value) {
        return {
            iconColor: 'grey-6',
            icon: 'sym_r_horizontal_rule',
            label: 'Movement unavailable'
        };
    }
    if ($props.trend.direction === 'stable') {
        return {
            iconColor: 'grey-6',
            icon: 'sym_r_trending_flat',
            label: 'Holding steady'
        };
    }
    const isUp = $props.trend.direction === 'up';
    return {
        iconColor: 'accent',
        icon: isUp ? 'sym_r_trending_up' : 'sym_r_trending_down',
        label: `Trending ${isUp ? 'up' : 'down'} ${Math.abs($props.trend.changePercent)}% a month`
    };
});

const trendBasis = computed(() => {
    if (!Number.isFinite($props.trend?.dataPoints)) {
        return null;
    }
    return `Fitted across ${formatMarketMetric($props.trend.dataPoints)} daily snapshots`
        + `${Number.isFinite($props.trend.windowDays) ? ` from the last ${$props.trend.windowDays} days` : ''}.`;
});

const trendTitle = computed(() => [trendPresentation.value.label, trendBasis.value].filter(Boolean).join('. '));
</script>

<template>
    <div class="market-trend">
        <q-chip
            :aria-label="trendTitle"
            :title="trendTitle"
            class="market-trend-badge font-mono q-ml-none"
            color="primary"
            outline
            role="status"
            square
        >
            <q-icon :color="trendPresentation.iconColor" :name="trendPresentation.icon" class="q-mr-xs" size="18px"/>
            {{ trendPresentation.label }}
        </q-chip>
        <p v-if="isUnavailable" class="text-caption text-hint q-mt-xs q-mb-none">
            Shown once seven daily snapshots from the last 30 days are held<MarketNoteMark :number="3"/>
        </p>
    </div>
</template>
